import type { Card } from '../../components/games/henhur/types/card.types';
import type {
  HenHurPlayerView,
  PlayerGameState,
  PlayerPublicState,
  BurnSlots,
  BurnSlot,
  TokenPool,
  TokenType,
  GamePhase,
} from '../../components/games/henhur/types/game.types';
import { BASE_CARDS, LAP1_CARDS } from './cardData';

// ============================================================================
// TYPES
// ============================================================================

export type HHCard = Card & { instanceId: string };

export interface HHConfig {
  turnsPerRound: number;
  handSize: number;
  trackLength: number;
  lapsToWin: number;
  maxTokens: number;
  selectedCards: string[];
}

export type HHPhase = 'race_selection' | 'auction_selection' | 'auction_drafting' | 'game_over';

interface HHPlayerDeck {
  playerId: string;
  deck: HHCard[];
  hand: HHCard[];
  discard: HHCard[];
}

interface HHBurnSlot {
  card: HHCard | null;
  slotIndex: number;
}

interface HHPlayer {
  playerId: string;
  playerName: string;
  isConnected: boolean;
  deck: HHPlayerDeck;
  position: { playerId: string; lane: 'inner' | 'middle' | 'outer'; space: number; lap: number };
  tokens: TokenPool;
  maxTokens: number;
  burnSlots: [HHBurnSlot, HHBurnSlot, HHBurnSlot];
  selectedCard: HHCard | null;
  selectedTokens: TokenType[];
  willBurn: boolean;
  isReady: boolean;
  priorityModifier: number;
  cardsPlayed: number;
  cardsBurned: number;
  distanceMoved: number;
}

interface HHSelection {
  card: HHCard;
  willBurn: boolean;
  tokensToUse: TokenType[];
}

export interface HHState {
  config: HHConfig;
  variant: 'standard' | 'debug';
  phase: HHPhase;
  roundNumber: number;
  turnNumber: number;
  turnType: 'race' | 'auction';
  players: HHPlayer[];
  raceSelections: Record<string, HHSelection>;
  auctionBids: Record<string, HHSelection>;
  auctionPool: HHCard[];
  auctionOrder: string[];
  currentDrafter: string | null;
  sharedAuctionDeck: HHCard[];
  winner: string | null;
  rng: number;
}

export type HHAction =
  | { type: 'select_race_card'; playerId: string; cardId: string; willBurn: boolean; tokensToUse: TokenType[] }
  | { type: 'select_auction_card'; playerId: string; cardId: string; willBurn: boolean; tokensToUse: TokenType[] }
  | { type: 'draft_card'; playerId: string; cardId: string };

export type HHPlayerView = HenHurPlayerView;

// ============================================================================
// RNG (mulberry32 seeded)
// ============================================================================

function rngStep(s: number): [number, number] {
  s = (s + 0x6d2b79f5) | 0;
  let t = Math.imul(s ^ (s >>> 15), 1 | s);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return [(t ^ (t >>> 14)) >>> 0, s];
}

function shuffle<T>(arr: T[], rng: number): [T[], number] {
  const result = [...arr];
  for (let i = result.length - 1; i > 0; i--) {
    let rand: number;
    [rand, rng] = rngStep(rng);
    const j = rand % (i + 1);
    [result[i], result[j]] = [result[j], result[i]];
  }
  return [result, rng];
}

function rollDie(sides: number, rng: number): [number, number] {
  let rand: number;
  [rand, rng] = rngStep(rng);
  return [(rand % sides) + 1, rng];
}

// ============================================================================
// CARD HELPERS
// ============================================================================

function expandCard(card: Card, prefix: string): HHCard[] {
  const copies = card.copies ?? 1;
  return Array.from({ length: copies }, (_, i) => ({ ...card, instanceId: `${prefix}_${card.id}_${i}` }));
}

function computePriority(card: HHCard, rng: number): [number, number] {
  const p = card.priority;
  if (typeof p === 'number') return [p, rng];
  const sides = parseInt(p.dice.replace('d', ''), 10) || 6;
  let roll: number;
  [roll, rng] = rollDie(sides, rng);
  return [p.base + roll, rng];
}

// ============================================================================
// DECK OPERATIONS
// ============================================================================

function drawFromDeck(
  deckState: HHPlayerDeck,
  count: number,
  rng: number,
): [HHPlayerDeck, number] {
  let d = { ...deckState, deck: [...deckState.deck], hand: [...deckState.hand], discard: [...deckState.discard] };
  for (let i = 0; i < count; i++) {
    if (d.deck.length === 0) {
      if (d.discard.length === 0) break;
      let shuffled: HHCard[];
      [shuffled, rng] = shuffle(d.discard as HHCard[], rng);
      d.deck = shuffled;
      d.discard = [];
    }
    const card = d.deck[0];
    d.deck = d.deck.slice(1);
    d.hand = [...d.hand, card];
  }
  return [d, rng];
}

function refillHandIfEmpty(player: HHPlayer, handSize: number, rng: number): [HHPlayer, number] {
  if (player.deck.hand.length > 0) return [player, rng];
  let newDeck: HHPlayerDeck;
  [newDeck, rng] = drawFromDeck(player.deck, handSize, rng);
  return [{ ...player, deck: newDeck }, rng];
}

// ============================================================================
// TOKEN HELPERS
// ============================================================================

function emptyTokenPool(): TokenPool {
  return { 'P+': 0, 'R+': 0, 'A+': 0, 'W+': 0, 'P+3': 0, 'D': 0 };
}

function startingTokenPool(): TokenPool {
  return { 'P+': 1, 'R+': 1, 'A+': 1, 'W+': 1, 'P+3': 0, 'D': 0 };
}

function tokenPriorityBonus(tokens: TokenType[]): number {
  return tokens.reduce((sum, t) => {
    if (t === 'P+') return sum + 1;
    if (t === 'P+3') return sum + 3;
    if (t === 'W+') return sum + 1;
    return sum;
  }, 0);
}

function tokenRaceBonus(tokens: TokenType[]): number {
  return tokens.reduce((sum, t) => {
    if (t === 'R+') return sum + 1;
    if (t === 'W+') return sum + 1;
    return sum;
  }, 0);
}

function tokenAuctionBonus(tokens: TokenType[]): number {
  return tokens.reduce((sum, t) => {
    if (t === 'A+') return sum + 1;
    if (t === 'W+') return sum + 1;
    return sum;
  }, 0);
}

function spendTokens(pool: TokenPool, tokens: TokenType[]): TokenPool {
  const result = { ...pool };
  for (const t of tokens) {
    if (t in result) (result as Record<string, number>)[t] = Math.max(0, ((result as Record<string, number>)[t] ?? 0) - 1);
  }
  return result;
}

function gainToken(pool: TokenPool, tokenType: string, count: number, maxTokens: number): TokenPool {
  const total = Object.values(pool).reduce((s, v) => s + v, 0);
  const canGain = Math.min(count, maxTokens - total);
  if (canGain <= 0) return pool;
  const result = { ...pool };
  const key = tokenType as keyof TokenPool;
  if (key in result) result[key] = (result[key] as number) + canGain;
  return result;
}

// ============================================================================
// EFFECT EXECUTION (pure, returns updated player + new rng)
// ============================================================================

function executeEffects(
  effects: { type: string; params: Record<string, unknown> }[],
  player: HHPlayer,
  trackLength: number,
  rng: number,
): [HHPlayer, number] {
  let p = player;
  for (const eff of effects) {
    switch (eff.type) {
      case 'move_player_position': {
        const dist = (eff.params.distance as number) || 0;
        p = movePlayer(p, dist, trackLength);
        break;
      }
      case 'affect_token_pool': {
        const { action, tokenType, count } = eff.params as { action: string; tokenType: string; count: number };
        if (action === 'gain') {
          p = { ...p, tokens: gainToken(p.tokens, tokenType, count, p.maxTokens) };
        } else if (action === 'spend') {
          const key = tokenType as keyof TokenPool;
          if (key in p.tokens) {
            p = { ...p, tokens: { ...p.tokens, [key]: Math.max(0, (p.tokens[key] as number) - count) } };
          }
        }
        break;
      }
      case 'draw_cards': {
        const drawCount = (eff.params.count as number) || 1;
        let newDeck: HHPlayerDeck;
        [newDeck, rng] = drawFromDeck(p.deck, drawCount, rng);
        p = { ...p, deck: newDeck };
        break;
      }
      case 'modify_priority': {
        const adj = (eff.params.adjustment as number) || 0;
        p = { ...p, priorityModifier: p.priorityModifier + adj };
        break;
      }
      // move_opponent_position and affect_player_mat are not implemented (require interactive target selection)
    }
  }
  return [p, rng];
}

function movePlayer(player: HHPlayer, spaces: number, trackLength: number): HHPlayer {
  let { space, lap } = player.position;
  space += spaces;
  while (space >= trackLength) { space -= trackLength; lap++; }
  while (space < 0) {
    if (lap > 1) { lap--; space += trackLength; }
    else { space = 0; break; }
  }
  return {
    ...player,
    position: { ...player.position, space, lap },
    distanceMoved: player.distanceMoved + Math.abs(spaces),
  };
}

// ============================================================================
// INIT
// ============================================================================

export const DEFAULT_CONFIG: HHConfig = {
  turnsPerRound: 8,
  handSize: 8,
  trackLength: 30,
  lapsToWin: 3,
  maxTokens: 10,
  selectedCards: [],
};

export function createGame(
  config: HHConfig,
  players: { id: string; name: string; isBot?: boolean }[],
  seed?: number,
): HHState {
  let rng = seed ?? Date.now();

  // Build player decks
  const gamePlayers: HHPlayer[] = players.map((p, pi) => {
    const baseCards = BASE_CARDS.flatMap(card => expandCard(card, `p${pi}`));
    let shuffledDeck: HHCard[];
    [shuffledDeck, rng] = shuffle(baseCards, rng);
    let deck: HHPlayerDeck = { playerId: p.id, deck: shuffledDeck, hand: [], discard: [] };
    [deck, rng] = drawFromDeck(deck, config.handSize, rng);
    return {
      playerId: p.id,
      playerName: p.name,
      isConnected: true,
      deck,
      position: { playerId: p.id, lane: 'middle', space: 0, lap: 1 },
      tokens: startingTokenPool(),
      maxTokens: config.maxTokens,
      burnSlots: [
        { card: null, slotIndex: 0 },
        { card: null, slotIndex: 1 },
        { card: null, slotIndex: 2 },
      ],
      selectedCard: null,
      selectedTokens: [],
      willBurn: false,
      isReady: false,
      priorityModifier: 0,
      cardsPlayed: 0,
      cardsBurned: 0,
      distanceMoved: 0,
    };
  });

  // Build shared auction deck from lap1 cards (filtered by selectedCards if any)
  const selectedIds = new Set(config.selectedCards);
  const eligible = LAP1_CARDS.filter(c => selectedIds.size === 0 || selectedIds.has(c.id));
  let auctionCards: HHCard[] = eligible.flatMap((card, ci) =>
    Array.from({ length: card.copies ?? 2 }, (_, i) => ({ ...card, instanceId: `a_${ci}_${card.id}_${i}` }))
  );
  [auctionCards, rng] = shuffle(auctionCards, rng);

  const numPoolCards = players.length + 1;
  const auctionPool = auctionCards.slice(0, numPoolCards);
  const sharedAuctionDeck = auctionCards.slice(numPoolCards);

  return {
    config,
    variant: 'standard',
    phase: 'race_selection',
    roundNumber: 1,
    turnNumber: 1,
    turnType: 'race',
    players: gamePlayers,
    raceSelections: {},
    auctionBids: {},
    auctionPool,
    auctionOrder: [],
    currentDrafter: null,
    sharedAuctionDeck,
    winner: null,
    rng,
  };
}

// ============================================================================
// ACTION PROCESSING
// ============================================================================

export function applyAction(state: HHState, action: HHAction): HHState {
  switch (action.type) {
    case 'select_race_card':
      return applySelectRaceCard(state, action);
    case 'select_auction_card':
      return applySelectAuctionCard(state, action);
    case 'draft_card':
      return applyDraftCard(state, action);
    default:
      return state;
  }
}

function applySelectRaceCard(
  state: HHState,
  action: Extract<HHAction, { type: 'select_race_card' }>,
): HHState {
  if (state.phase !== 'race_selection') return state;
  const playerIdx = state.players.findIndex(p => p.playerId === action.playerId);
  if (playerIdx === -1) return state;
  const player = state.players[playerIdx];
  if (player.isReady) return state;

  const card = player.deck.hand.find(c => c.id === action.cardId);
  if (!card) return state;

  const updatedPlayer: HHPlayer = {
    ...player,
    selectedCard: card,
    selectedTokens: action.tokensToUse,
    willBurn: action.willBurn,
    isReady: true,
  };

  const updatedPlayers = state.players.map((p, i) => i === playerIdx ? updatedPlayer : p);
  const updatedSelections = {
    ...state.raceSelections,
    [action.playerId]: { card, willBurn: action.willBurn, tokensToUse: action.tokensToUse },
  };

  let next: HHState = { ...state, players: updatedPlayers, raceSelections: updatedSelections };

  // If all players ready, resolve immediately
  if (updatedPlayers.every(p => p.isReady)) {
    next = resolveRaceTurn(next);
  }

  return next;
}

function applySelectAuctionCard(
  state: HHState,
  action: Extract<HHAction, { type: 'select_auction_card' }>,
): HHState {
  if (state.phase !== 'auction_selection') return state;
  const playerIdx = state.players.findIndex(p => p.playerId === action.playerId);
  if (playerIdx === -1) return state;
  const player = state.players[playerIdx];
  if (player.isReady) return state;

  const card = player.deck.hand.find(c => c.id === action.cardId);
  if (!card) return state;

  const updatedPlayer: HHPlayer = {
    ...player,
    selectedCard: card,
    selectedTokens: action.tokensToUse,
    willBurn: action.willBurn,
    isReady: true,
  };

  const updatedPlayers = state.players.map((p, i) => i === playerIdx ? updatedPlayer : p);
  const updatedBids = {
    ...state.auctionBids,
    [action.playerId]: { card, willBurn: action.willBurn, tokensToUse: action.tokensToUse },
  };

  let next: HHState = { ...state, players: updatedPlayers, auctionBids: updatedBids };

  if (updatedPlayers.every(p => p.isReady)) {
    next = resolveAuctionBids(next);
  }

  return next;
}

function applyDraftCard(
  state: HHState,
  action: Extract<HHAction, { type: 'draft_card' }>,
): HHState {
  if (state.phase !== 'auction_drafting') return state;
  if (state.currentDrafter !== action.playerId) return state;

  const playerIdx = state.players.findIndex(p => p.playerId === action.playerId);
  if (playerIdx === -1) return state;

  const cardIdx = state.auctionPool.findIndex(c => c.id === action.cardId);
  if (cardIdx === -1) return state;

  const card = state.auctionPool[cardIdx];
  const newPool = state.auctionPool.filter((_, i) => i !== cardIdx);

  const player = state.players[playerIdx];
  const newPlayerDeck = {
    ...player.deck,
    deck: [card, ...player.deck.deck],
  };
  const updatedPlayer = { ...player, deck: newPlayerDeck };
  const updatedPlayers = state.players.map((p, i) => i === playerIdx ? updatedPlayer : p);

  let next: HHState = { ...state, players: updatedPlayers, auctionPool: newPool };

  // Advance to next drafter
  const draftOrderIdx = state.auctionOrder.indexOf(action.playerId);
  if (draftOrderIdx + 1 >= state.auctionOrder.length) {
    // All drafted — advance to next turn
    next = advanceToNextTurn({ ...next, auctionPool: [], currentDrafter: null });
  } else {
    next = { ...next, currentDrafter: state.auctionOrder[draftOrderIdx + 1] };
  }

  return next;
}

// ============================================================================
// RESOLUTION
// ============================================================================

function resolveRaceTurn(state: HHState): HHState {
  const selections = Object.entries(state.raceSelections);
  let rng = state.rng;

  // Calculate priorities
  const withPriorities = selections.map(([playerId, sel]) => {
    let rolledPriority: number;
    [rolledPriority, rng] = computePriority(sel.card, rng);
    const playerObj = state.players.find(p => p.playerId === playerId)!;
    const tokenBonus = tokenPriorityBonus(sel.tokensToUse);
    return { playerId, sel, totalPriority: rolledPriority + playerObj.priorityModifier + tokenBonus };
  });

  withPriorities.sort((a, b) => b.totalPriority - a.totalPriority);

  // Execute each player's card in priority order
  let players = [...state.players];
  for (const { playerId, sel } of withPriorities) {
    let pidx = players.findIndex(p => p.playerId === playerId);
    if (pidx === -1) continue;
    let player = players[pidx];
    const { card, willBurn, tokensToUse } = sel;

    // Movement from raceNumber + R+/W+ tokens
    const movement = card.raceNumber + tokenRaceBonus(tokensToUse);
    player = movePlayer(player, movement, state.config.trackLength);

    // Remove card from hand
    const handWithout = player.deck.hand.filter(c => c.id !== card.id || c.instanceId !== card.instanceId);
    // If not found by instanceId, remove first matching id
    const finalHand = handWithout.length < player.deck.hand.length
      ? handWithout
      : player.deck.hand.filter((c, i) => !(c.id === card.id && i === player.deck.hand.findIndex(x => x.id === card.id)));

    if (willBurn) {
      const slotIdx = player.burnSlots.findIndex(s => s.card === null);
      const newSlots = player.burnSlots.map((s, i) =>
        i === slotIdx && slotIdx !== -1 ? { ...s, card: card as unknown as (HHCard) } : s
      ) as [HHBurnSlot, HHBurnSlot, HHBurnSlot];
      player = { ...player, deck: { ...player.deck, hand: finalHand }, burnSlots: newSlots, cardsBurned: player.cardsBurned + 1 };
      // Execute burn effects
      [player, rng] = executeEffects(card.burnEffect, player, state.config.trackLength, rng);
    } else {
      player = { ...player, deck: { ...player.deck, hand: finalHand, discard: [...player.deck.discard, card] } };
      // Execute normal effects (skip move_player_position — already moved by raceNumber)
      const nonMoveEffects = card.effect.filter(e => e.type !== 'move_player_position');
      [player, rng] = executeEffects(nonMoveEffects, player, state.config.trackLength, rng);
    }

    // Use tokens
    player = { ...player, tokens: spendTokens(player.tokens, tokensToUse) };
    // Refill hand if empty
    [player, rng] = refillHandIfEmpty(player, state.config.handSize, rng);

    player = { ...player, cardsPlayed: player.cardsPlayed + 1, selectedCard: null, selectedTokens: [], willBurn: false, isReady: false, priorityModifier: 0 };
    players[pidx] = player;
  }

  // Check win condition
  let winner = state.winner;
  const finishers = players.filter(p => p.position.lap > state.config.lapsToWin);
  if (finishers.length > 0 && !winner) {
    finishers.sort((a, b) => b.position.space - a.position.space);
    winner = finishers[0].playerId;
  }

  const next = { ...state, players, raceSelections: {}, rng, winner };
  if (winner) return { ...next, phase: 'game_over' };
  return advanceToNextTurn(next);
}

function resolveAuctionBids(state: HHState): HHState {
  const bids = Object.entries(state.auctionBids);
  let rng = state.rng;

  const withValues = bids.map(([playerId, bid]) => {
    let rolledPriority: number;
    [rolledPriority, rng] = computePriority(bid.card, rng);
    const trickValue = bid.card.trickNumber + tokenAuctionBonus(bid.tokensToUse);
    return { playerId, bid, trickValue, rolledPriority };
  });

  withValues.sort((a, b) => b.trickValue !== a.trickValue ? b.trickValue - a.trickValue : b.rolledPriority - a.rolledPriority);

  const auctionOrder = withValues.map(w => w.playerId);

  // Discard/burn bid cards and spend tokens
  let players = [...state.players];
  for (const { playerId, bid } of withValues) {
    let pidx = players.findIndex(p => p.playerId === playerId);
    if (pidx === -1) continue;
    let player = players[pidx];
    const { card, willBurn, tokensToUse } = bid;

    const finalHand = player.deck.hand.filter((c, i) => !(c.id === card.id && i === player.deck.hand.findIndex(x => x.id === card.id)));

    if (willBurn) {
      const slotIdx = player.burnSlots.findIndex(s => s.card === null);
      const newSlots = player.burnSlots.map((s, i) =>
        i === slotIdx && slotIdx !== -1 ? { ...s, card: card as unknown as HHCard } : s
      ) as [HHBurnSlot, HHBurnSlot, HHBurnSlot];
      player = { ...player, deck: { ...player.deck, hand: finalHand }, burnSlots: newSlots, cardsBurned: player.cardsBurned + 1 };
    } else {
      player = { ...player, deck: { ...player.deck, hand: finalHand, discard: [...player.deck.discard, card] } };
    }

    player = { ...player, tokens: spendTokens(player.tokens, tokensToUse) };
    [player, rng] = refillHandIfEmpty(player, state.config.handSize, rng);
    player = { ...player, cardsPlayed: player.cardsPlayed + 1, selectedCard: null, selectedTokens: [], willBurn: false, isReady: false };
    players[pidx] = player;
  }

  return {
    ...state,
    players,
    auctionBids: {},
    auctionOrder,
    currentDrafter: auctionOrder[0] ?? null,
    phase: 'auction_drafting',
    rng,
  };
}

function advanceToNextTurn(state: HHState): HHState {
  let turnNumber = state.turnNumber + 1;
  let roundNumber = state.roundNumber;

  if (turnNumber > state.config.turnsPerRound) {
    roundNumber++;
    turnNumber = 1;
  }

  const turnType: 'race' | 'auction' = turnNumber % 2 === 1 ? 'race' : 'auction';
  const phase: HHPhase = turnType === 'race' ? 'race_selection' : 'auction_selection';

  // Reset all players for new turn
  const players = state.players.map(p => ({ ...p, isReady: false, selectedCard: null, selectedTokens: [], willBurn: false }));

  let next: HHState = {
    ...state,
    players,
    turnNumber,
    roundNumber,
    turnType,
    phase,
    auctionOrder: [],
    currentDrafter: null,
    raceSelections: {},
    auctionBids: {},
  };

  // For auction turns, reveal new pool
  if (turnType === 'auction') {
    next = refreshAuctionPool(next);
  }

  return next;
}

function refreshAuctionPool(state: HHState): HHState {
  const numCards = state.players.length + 1;
  let deck = [...state.sharedAuctionDeck];

  // Refill deck if running low
  if (deck.length < numCards) {
    const selectedIds = new Set(state.config.selectedCards);
    const eligible = LAP1_CARDS.filter(c => selectedIds.size === 0 || selectedIds.has(c.id));
    const refillCards: HHCard[] = eligible.flatMap((card, ci) =>
      Array.from({ length: card.copies ?? 2 }, (_, i) => ({ ...card, instanceId: `ar_${ci}_${card.id}_${i}_${state.roundNumber}` }))
    );
    let shuffled: HHCard[];
    let rng = state.rng;
    [shuffled, rng] = shuffle(refillCards, rng);
    deck = [...deck, ...shuffled];
    return refreshAuctionPool({ ...state, sharedAuctionDeck: deck, rng });
  }

  const pool = deck.splice(0, numCards);
  return { ...state, auctionPool: pool, sharedAuctionDeck: deck };
}

// ============================================================================
// PLAYER VIEW
// ============================================================================

export function getPlayerView(state: HHState, playerId: string | null): HHPlayerView {
  const targetId = playerId ?? state.players[0]?.playerId ?? '';
  const player = state.players.find(p => p.playerId === targetId);

  const myState = player ?? state.players[0];

  const otherPlayers: PlayerPublicState[] = state.players
    .filter(p => p.playerId !== (player?.playerId ?? targetId))
    .map(p => ({
      playerId: p.playerId,
      playerName: p.playerName,
      isConnected: p.isConnected,
      position: p.position,
      handCount: p.deck.hand.length,
      deckCount: p.deck.deck.length,
      discardCount: p.deck.discard.length,
      tokens: p.tokens,
      burnSlots: p.burnSlots as unknown as BurnSlots,
      isReady: p.isReady,
      cardsPlayed: p.cardsPlayed,
      cardsBurned: p.cardsBurned,
      distanceMoved: p.distanceMoved,
    }));

  const readyPlayers = state.players.filter(p => p.isReady).map(p => p.playerId);

  return {
    type: 'henhur',
    started: true,
    variant: state.variant,
    phase: state.phase as GamePhase,
    turnNumber: state.turnNumber,
    roundNumber: state.roundNumber,
    turnType: state.turnType,
    track: { trackLength: state.config.trackLength, lapsToWin: state.config.lapsToWin },
    myState: myState as unknown as PlayerGameState,
    otherPlayers,
    auctionPool: state.auctionPool as unknown as Card[],
    auctionOrder: state.auctionOrder,
    currentDrafter: state.currentDrafter ?? undefined,
    readyPlayers,
    winner: state.winner,
  };
}

// ============================================================================
// VALID ACTIONS
// ============================================================================

export function getValidActions(state: HHState, playerId: string): HHAction[] {
  const player = state.players.find(p => p.playerId === playerId);
  if (!player || player.isReady) return [];

  if (state.phase === 'race_selection') {
    return player.deck.hand.map(card => ({
      type: 'select_race_card' as const,
      playerId,
      cardId: card.id,
      willBurn: false,
      tokensToUse: [],
    }));
  }

  if (state.phase === 'auction_selection') {
    return player.deck.hand.map(card => ({
      type: 'select_auction_card' as const,
      playerId,
      cardId: card.id,
      willBurn: false,
      tokensToUse: [],
    }));
  }

  if (state.phase === 'auction_drafting' && state.currentDrafter === playerId) {
    return state.auctionPool.map(card => ({
      type: 'draft_card' as const,
      playerId,
      cardId: card.id,
    }));
  }

  return [];
}

// ============================================================================
// BOT ACTION
// ============================================================================

export function getBotAction(playerId: string, state: HHState): HHAction | null {
  const actions = getValidActions(state, playerId);
  return actions[0] ?? null;
}

// ============================================================================
// DESCRIBE
// ============================================================================

export function describe(config: HHConfig): string {
  return [
    `HenHur — Racing and auction card game. Win by completing ${config.lapsToWin} laps of ${config.trackLength} spaces.`,
    '',
    `Each round has ${config.turnsPerRound} turns. Odd turns are Race turns; even turns are Auction turns. Hand size: ${config.handSize}.`,
    '',
    'Race turns (simultaneous):',
    '  All players secretly select a card from hand, choose whether to Burn it, and optionally spend tokens.',
    '  Cards resolve in priority order (card priority value + P+/W+ tokens spent).',
    '  Movement = the card\'s race number + R+/W+ tokens spent.',
    '  Normal play: card effect fires, card goes to discard.',
    '  Burn: the card\'s burn effect happens instead; the card goes to a burn slot (max 3 slots). Burn effects are usually stronger.',
    '  Refill hand automatically when empty.',
    '',
    'Auction turns (simultaneous, then sequential):',
    '  All players secretly bid with a hand card + optional tokens.',
    '  Bid value = the card\'s trick number + A+/W+ tokens spent.',
    '  Highest bidder drafts first from the shared pool (N+1 cards for N players); ties broken by card priority.',
    '  Draft order continues until all players have picked one upgrade card for their deck.',
    '',
    'Tokens (each player starts with 1 of each: P+, R+, A+, W+):',
    '  P+ — +1 to priority this turn.',
    '  R+ — +1 movement on a race turn.',
    '  A+ — +1 bid value on an auction turn.',
    '  W+ — +1 to priority AND +1 to race movement or auction bid (wild).',
    '  P+3 — +3 to priority (rare).',
    `  Token pool capped at ${config.maxTokens} total.`,
    '',
    `First player to complete lap ${config.lapsToWin} wins.`,
  ].join('\n');
}
